import { useContext } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { stringify } from 'querystring';
import classNames from 'classnames';

import { commonMetatags } from 'consts/metatags';
import { Filter } from 'types/Filter';
import MappedSearchFiltersMeta from 'types/MappedSearchFiltersMeta';
import { ConfigContext } from '@providers/ConfigProvider';
import { FilesContext } from '@providers/FilesProvider';
import SearchController, { useSearchState, FallbackProps } from '@components/SearchController';
import Typography from '@components/atoms/Typography';
import Truncate from '@templates/Truncate';
import resolveImageUrl from 'utils/resolveImageUrl';
import getVariantThumbnail from 'utils/getVariantThumbnail';
import getSortedQueryParams from 'utils/getSortedQueryParams';
import resolvePath from 'utils/resolvePath';
import { useIntl, FormattedMessage } from 'utils/intl';
import omit from 'utils/omit';

import classes from './Search.module.scss';
import messages from './Search.messages';
import { getMetaDescription, getPageTitleAdditionalInfo } from './Search.utils';

type Props = FallbackProps & {
  categoryName?: string;
  searchName?: string;
  productName?: string;
  manufacturer?: string;
  family?: string;
  meta?: MappedSearchFiltersMeta;
  filters?: Filter[];
  filtersProductTypeId?: Nullable<number>;
};

type Data = Exclude<Props['data'], undefined>;

const Search = ({
  productName = '',
  searchName = '',
  family = '',
  manufacturer = '',
  categoryName = '',
  filters,
  filtersProductTypeId,
  meta = { exactMatches: 0, exactMatchesLowCountIsOk: false },
  ...props
}: Props) => {
  const { data = [], mainProductTypeId, productTypes } = props;
  const searchState = useSearchState({ filters, mainProductTypeId, filtersProductTypeId, productTypes });
  const { filterTags, filtersQuery } = searchState;
  const router = useRouter();
  const intl = useIntl();

  const { appUrl, contact, searchMinExactMatches } = useContext(ConfigContext);
  const { getShopImageConfigs } = useContext(FilesContext);
  const [logoOg] = getShopImageConfigs(['logo-og']);
  const logoOgUrl = logoOg ? resolveImageUrl(logoOg) : '';

  const canonicalUrl = `${appUrl}${router.pathname}?${stringify(
    getSortedQueryParams(omit(router.query, ['sort_id']))
  )}`;
  const titleAdditionalInfo = getPageTitleAdditionalInfo(intl, router.query);

  const firstVariant = resolvePath<Data, Data[0]['variant']>(data as Data, '[0].variant');
  const firstVariantThumbnail = firstVariant ? getVariantThumbnail(firstVariant) : undefined;
  const firstProductOgImageUrl = firstVariantThumbnail
    ? resolveImageUrl({
        preset: 'og',
        name: firstVariantThumbnail.basename,
        description: 'logo-og',
        extension: firstVariantThumbnail.extension,
        productName: firstVariant.product.name,
      })
    : undefined;

  const hasResults = !!data.length;
  const titleSuffix = productName || family || manufacturer || filterTags[0]?.values;
  const categoryNameWithSuffix = `${categoryName}${titleSuffix ? ` ${titleSuffix}` : ''}`;
  const title = searchName ? intl.formatMessage(messages.searchResults, { searchName }) : categoryNameWithSuffix;
  const wasQueryCorrected = meta.correctedQuery && meta.correctedQuery.toLowerCase() !== searchName.toLowerCase();
  const isExactMatchesLowCountOk = !!meta?.exactMatchesLowCountIsOk;

  const queryTitle = (
    <>
      {!isExactMatchesLowCountOk && wasQueryCorrected ? (
        <FormattedMessage {...messages.didYouMean} values={{ correctedQuery: meta.correctedQuery }} />
      ) : (
        <FormattedMessage
          {...(isExactMatchesLowCountOk || meta.exactMatches > searchMinExactMatches
            ? messages.searchResults
            : messages.lackOfExactMatch)}
          values={{ searchName }}
        />
      )}
      {!isExactMatchesLowCountOk && meta.exactMatches <= searchMinExactMatches && (
        <Typography variant="h4" renderAs="div" className={classes.suggestedResults}>
          {wasQueryCorrected ? (
            <FormattedMessage {...messages.lackOfResults} values={{ searchName }} />
          ) : (
            <FormattedMessage {...messages.seeSuggestedResults} />
          )}
        </Typography>
      )}
    </>
  );

  const header = (
    <Typography
      variant="h1"
      className={classNames(classes.header, {
        [classes.notFound]: !hasResults,
        [classes.withSpacing]: !!filterTags.length,
      })}
    >
      {hasResults ? (
        <>
          {!!categoryNameWithSuffix && !searchName ? (
            <Truncate title={categoryNameWithSuffix} fluid>
              {categoryNameWithSuffix}
            </Truncate>
          ) : (
            queryTitle
          )}
        </>
      ) : (
        <FormattedMessage
          {...(searchName ? messages.notFoundResults : messages.notFoundResultsWithoutQuery)}
          values={{ searchName }}
        />
      )}
    </Typography>
  );

  return (
    <>
      <Head>
        {commonMetatags(
          contact,
          canonicalUrl,
          `${title}${titleAdditionalInfo}`,
          getMetaDescription(intl, contact, title, filtersQuery),
          firstProductOgImageUrl || logoOgUrl,
          true,
          true
        )}
      </Head>
      <SearchController {...props} {...searchState} header={header} />
    </>
  );
};

export default Search;
